import { Set } from 'immutable';

import { NotebookTypes } from '@mode/notebooks/contract';
import { NotebookResource, SessionResource } from '@mode/legacy/data-access-model-js';
import { NotebookActionType } from '@mode/legacy/util-domain';
import { CellExportActions } from '@mode/notebooks/contract';
export namespace NotebookActions {
  export const Type = NotebookActionType;

  export interface Idle {
    type: NotebookActionType.Idle;
    notebookToken: string;
  }

  export interface SessionBusy {
    type: NotebookActionType.SessionBusy;
    notebookToken: string;
  }

  export interface SessionMetrics {
    type: NotebookActionType.SessionMetrics;
    metrics: NotebookTypes.Metrics;
    notebookToken: string;
    notebookResource?: NotebookResource;
  }

  export interface DataLoaded {
    type: NotebookActionType.DataLoaded;
    notebookToken: string;
  }

  export interface Disconnected {
    type: NotebookActionType.Disconnected;
  }

  export interface Terminated {
    type: NotebookActionType.Terminated;
    sessionToken: string;
    notebookToken: string;
    terminationReason: string;
  }

  export interface ClearAllCellOutputs {
    type: NotebookActionType.ClearAllCellOutputs;
    notebookToken: string;
  }

  export interface NoSession {
    type: NotebookActionType.NoSession;
    notebookToken: string;
    error?: Error; // might fire from exception, maybe not though
  }

  export interface AddNotebook {
    type: NotebookActionType.Add;
    reportToken: string;
  }

  export interface AddNotebookFailure {
    type: NotebookActionType.AddFailure;
    reportToken: string;
    error: Error;
  }

  export interface AddNotebookSuccess {
    type: NotebookActionType.AddSuccess;
    notebookResource: NotebookResource;
  }

  export interface AddNotebookResourcesOnSuccess {
    type: NotebookActionType.AddNotebookResourcesOnSuccess;
    notebookResource: NotebookResource;
  }

  export interface SaveReport {
    type: NotebookActionType.SaveReport;
    reportRun: any;
    report: any;
  }

  export interface Init {
    type: NotebookActionType.Init;
    // notebookToken: string; // TODO: Use token when UI supports multiple notebooks
  }

  export interface InitComplete {
    type: NotebookActionType.InitComplete;
    notebookToken: string;
  }

  export interface LoadWithInit {
    type: NotebookActionType.LoadWithInit;
    notebookToken: string;
  }

  export interface LoadWithInitSuccess {
    type: NotebookActionType.LoadWithInitSuccess;
    notebookResource: NotebookResource;
  }

  export interface Run {
    type: NotebookActionType.Run;
    notebookToken: string;
    runAllCells: boolean;
    cellTokensToRun: string[];
  }

  export interface RunFailed {
    type: NotebookActionType.RunFailed;
    notebookToken: string;
    error: Error;
  }

  export interface CancelRun {
    type: NotebookActionType.CancelRun;
    notebookToken: string;
  }

  export interface CancelRunComplete {
    type: NotebookActionType.CancelRunComplete;
    notebookToken: string;
  }

  export interface CancelRunSuccess {
    type: NotebookActionType.CancelRunSuccess;
    notebookToken: string;
  }

  export interface CreateRunSuccess {
    type: NotebookActionType.CreateRunSuccess;
    runToken: string;
    notebookToken: string;
    resultTokens: Set<any>;
  }

  export interface InitialRunSuccess {
    type: NotebookActionType.InitialRunSuccess;
    notebookToken: string;
  }

  export interface LoadFromSelectedReport {
    type: NotebookActionType.LoadFromSelectedReport;
  }

  export interface LoadFailure {
    type: NotebookActionType.LoadFailure;
    notebookToken: string;
    error: Error;
  }

  export interface LoadSuccess {
    type: NotebookActionType.LoadSuccess;
    notebookResource: NotebookResource;
  }

  export interface UpdateType {
    type: NotebookActionType.UpdateType;
    notebookToken: string;
    notebookType: NotebookTypes.NotebookType;
  }

  export interface UpdateTypeFailure {
    type: NotebookActionType.UpdateTypeFailure;
    error: Error;
    original: NotebookResource;
  }

  export interface UpdateSuccess {
    type: NotebookActionType.UpdateSuccess;
    notebookResource: NotebookResource;
  }

  export interface Destroy {
    type: NotebookActionType.Destroy;
    notebookToken: string;
  }

  export interface InitSession {
    type: NotebookActionType.InitSession;
    notebookToken: string;
    reuseIfActive: boolean; // reuse an existing active session, if possible
    runNewCells?: boolean;
  }

  export interface InitSessionFailure {
    type: NotebookActionType.InitSessionFailure;
    notebookToken: string;
    error: Error;
  }

  export interface VerifySession {
    type: NotebookActionType.VerifySession;
    runToken: string;
    session: SessionResource;
  }

  export interface LoadSessionSuccess {
    type: NotebookActionType.LoadSessionSuccess;
    notebookToken: string;
    runToken: string;
    sessionResource: SessionResource;
  }

  export interface RefreshSessionReportRun {
    type: NotebookActionType.RefreshSessionReportRun;
    notebookToken: string;
  }

  export interface PollSession {
    type: NotebookActionType.PollSession;
    notebookToken: string;
  }
}

export type NotebookActions =
  | NotebookActions.AddNotebook
  | NotebookActions.AddNotebookFailure
  | NotebookActions.AddNotebookSuccess
  | NotebookActions.Idle
  | NotebookActions.SessionBusy
  | NotebookActions.SessionMetrics
  | NotebookActions.DataLoaded
  | NotebookActions.Terminated
  | NotebookActions.NoSession
  | NotebookActions.Disconnected
  | NotebookActions.ClearAllCellOutputs
  | NotebookActions.Init
  | NotebookActions.InitComplete
  | NotebookActions.LoadWithInit
  | NotebookActions.LoadWithInitSuccess
  | NotebookActions.LoadFromSelectedReport
  | NotebookActions.LoadFailure
  | NotebookActions.LoadSuccess
  | NotebookActions.UpdateType
  | NotebookActions.UpdateTypeFailure
  | NotebookActions.UpdateSuccess
  | NotebookActions.Run
  | NotebookActions.RunFailed
  | NotebookActions.CreateRunSuccess
  | NotebookActions.InitialRunSuccess
  | NotebookActions.Destroy
  | NotebookActions.InitSession
  | NotebookActions.InitSessionFailure
  | NotebookActions.VerifySession
  | NotebookActions.LoadSessionSuccess
  | NotebookActions.PollSession
  | NotebookActions.CancelRun
  | NotebookActions.CancelRunComplete
  | NotebookActions.CancelRunSuccess
  | NotebookActions.RefreshSessionReportRun
  | NotebookActions.SaveReport
  | NotebookActions.AddNotebookResourcesOnSuccess;
